/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import React, { FC } from 'react';
import { Check, CloseThin, ChevronDown } from '~/components/Icons/fill';
import { Modals, Product } from '~/types/products';
import { AddOnStyled } from './AddOnStyled';
import { addOnData } from './addOnData';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { trackEvent } from '~/helpers/tracking';
import setHtml from '~/helpers/setHtml';
import useMediaQuery from '~/hooks/useMediaQuery';
import LegalAnnotation from '~/components/LegalAnnotation';
import * as entityId from '~/constants/entityIds';
import Tooltip from '~/components/Tooltip';
import { InfoCircle } from '~/components/Icons/outline';
import { isNonEmptyString } from '~/helpers/stringHelpers';

interface Props {
  selectedProduct: Product;
  setActiveModal: (key?: Modals) => void;
  visible: boolean;
}

const AddOnModal: FC<Props> = ({ selectedProduct, setActiveModal, visible }) => {
  const { legal } = useSelector((store: ReduxState) => store);
  const { legalMap } = legal || {};
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (!addOnData || !visible) return null;

  const { productId } = selectedProduct || {};
  const isYoutubetv = productId === entityId.YTTV_BASIC;
  const isUnlimitedVoice = productId === entityId.UNLIMITED_VOICE;

  const { title = '', image, description = '', content = {}, subDescription = '' } = addOnData[productId] || {};
  const { checklist = [], contentImage = {} } = content;

  const getHeader = () => {
    return (
      <>
        <img alt={image?.alt || ''} {...image} />
        {title && (
          <h3>
            <span {...setHtml(title)} />
          </h3>
        )}
      </>
    );
  };

  const getDisclosure = () => {
    if (isYoutubetv) {
      return legalMap?.youtubeTv?.text;
    } else if (isUnlimitedVoice) {
      return legalMap?.unlimitedDigitalVoice?.text;
    }
  };

  const handleDropdownTracking = () => {
    const dropdownElement = document.getElementById('js-details-container');
    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'VAS Modal',
        position: 'vas modal',
        location: 'YTTV VAS Modal - Dropdown',
        text: dropdownElement.hasAttribute('open') ? 'Close' : 'Open',
      },
    });
  };

  const handleLinkoffTracking = () => {
    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'VAS Modal',
        position: 'vas modal',
        location: 'YTTV VAS Modal - Channel List Link',
        text: content?.channels?.link?.text || 'View full channel list',
      },
    });
  };

  return (
    // only YTTV modal needs to be scrollable
    <AddOnStyled
      isScrollable={isYoutubetv}
      bold={checklist?.items?.length}
      useBoldHeading={checklist.useBoldHeading}
      useCenteredHeading={checklist.useCenteredHeading}
      hasDescription={isNonEmptyString(description)}
    >
      <CloseThin onClick={() => setActiveModal()} data-testid="modal-btn-close" />
      <div className="addon-screen__product">
        <header className="addon-screen__header">{getHeader()}</header>
        <div className="addon-screen__container" data-testid="addon-screen__container">
          {description && <p className="addon-screen__description">{description}</p>}
          {content?.subheading && <h4 className="addon-screen__subheading">{content?.subheading}</h4>}
          {content?.subcopy && (
            <p className="addon-screen__subcopy">
              {content?.subcopy}
              {content?.subcopyLegal && <LegalAnnotation legalKey={content?.subcopyLegal} />}
            </p>
          )}
          <div className="addon-screen__content">
            {contentImage?.src && <img alt={contentImage?.alt || ''} {...contentImage} />}
            <div>
              {checklist?.heading && <h4 className="addon-screen__checklistHeading">{checklist?.heading}</h4>}
              {checklist?.items?.map((item, index) => {
                return (
                  <div
                    key={index}
                    className={`addon-screen__checklist ${
                      checklist.useCheckIcons && 'addon-screen__checklist--indented'
                    }`}
                    data-testid="addon-screen__checklist"
                  >
                    {checklist?.items?.length >= 2 && checklist.useCheckIcons && (
                      <div className="addon-screen__checklistCheck">
                        <Check />
                      </div>
                    )}
                    <div>
                      {item?.heading && <h5 className="addon-screen__checklistItemHeading">{item?.heading}</h5>}
                      <p>
                        {item?.headingInline && (
                          <span className="addon-screen__checklistItemHeading">{item?.headingInline}</span>
                        )}
                        {item?.text}
                        {item?.tooltip && (
                          <Tooltip
                            interactive
                            position="top"
                            theme="light"
                            html={
                              <div style={{ width: 240 }}>
                                <div className="tooltip-info">
                                  <span {...setHtml(item?.tooltip)} />
                                </div>
                              </div>
                            }
                          >
                            <InfoCircle />
                          </Tooltip>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
              {/* GDT: Show disclaimer within modal for YouTube TV and Home Phone */}
              {window?.location?.href?.includes('/my-custom-quote') && (isYoutubetv || isUnlimitedVoice) && (
                <p className="legal" style={{ fontSize: '10px' }} {...setHtml(getDisclosure())} />
              )}
            </div>
          </div>
          {subDescription && <p>{subDescription}</p>}

          {content?.channels && (
            <div className="addon-screen__channels">
              <h4 className="addon-screen__channelsHeading">{content?.channels?.heading}</h4>
              {content?.channels?.image && (
                <img
                  alt={content?.channels?.image?.alt || ''}
                  width={isMobile ? content?.channels?.image?.mobileWidth : content?.channels?.image?.width}
                  src={content?.channels?.image?.src}
                />
              )}
              <a
                onClick={handleLinkoffTracking}
                className="addon-screen__channelsLink"
                href={content?.channels?.link?.href}
                target="_blank"
                rel="noreferrer"
              >
                {content?.channels?.link?.text}
              </a>
            </div>
          )}

          {content?.setupInfo && (
            <details id="js-details-container" className="addon-screen__setupinfo">
              <summary onClick={handleDropdownTracking} role="none" className="addon-screen__setupinfoHeading">
                {content?.setupInfo?.heading} <ChevronDown className="addon-screen__setupinfoHeadingIcon" />
              </summary>
              <ol className="addon-screen__setupinfoList">
                {content?.setupInfo?.items?.map((item, index) => {
                  return (
                    <li key={index} className="addon-screen__setupinfoListItem">
                      <span>{item}</span>
                    </li>
                  );
                })}
              </ol>
            </details>
          )}
        </div>
      </div>
    </AddOnStyled>
  );
};

AddOnModal.displayName = 'AddOnModal';

export default AddOnModal;
